/**
 * Threat Intelligence Types
 * 
 * TypeScript interfaces matching the backend ChatGPT-powered threat intelligence responses.
 * These types ensure type safety when consuming the new dynamic threat intelligence API.
 */

// Severity levels for threat insights
export type ThreatSeverity = "critical" | "high" | "medium" | "low";

// Individual threat insight from ChatGPT
export interface ThreatInsight {
  category: string;
  severity: ThreatSeverity;
  title: string;
  description: string;
  indicators: string[];
  mitigations: string[];
  lastUpdated: string;
}

// Summary of overall threat landscape
export interface ThreatSummary {
  overview: string;
  totalThreats: number;
  criticalCount: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  topThreats: ThreatInsight[];
  trendAnalysis: string;
  recommendations: string[];
}

// Category-specific threat data with AI insights
export interface CategoryThreatData {
  category: string;
  displayName: string;
  total: number;
  active: number;
  inactive: number;
  recentActivity: number;
  insights: ThreatInsight[];
  summary: string;
}

// Full dashboard threat overview response
export interface DashboardThreatOverview {
  generatedAt: string;
  summary: ThreatSummary;
  categories: CategoryThreatData[];
  alerts: string[];
}

// Lightweight threat summary response
export interface ThreatSummaryResponse {
  generatedAt: string;
  totals: {
    totalActive: number;
    totalInactive: number;
    totalRecent: number;
    total: number;
  };
  overview: string;
  trendAnalysis: string;
  topThreats: ThreatInsight[];
  recommendations: string[];
  alerts: string[];
}

// Category comparison data
export interface CategoryComparisonItem {
  category: string;
  active: number;
  inactive: number;
  total: number;
  recentActivity: number;
  percentageOfTotal: string;
  activityRate: string;
}

export interface CategoryComparisonResponse {
  generatedAt: string;
  totalThreats: number;
  categories: CategoryComparisonItem[];
}

// Refresh insights response
export interface RefreshInsightsResponse {
  success: boolean;
  message: string;
  generatedAt: string;
}

// Valid category keys for API requests
export type ThreatCategoryKey = "ransomware" | "phishing" | "malware" | "social-media";

// Mapping for display purposes
export const THREAT_CATEGORY_DISPLAY_NAMES: Record<ThreatCategoryKey, string> = {
  ransomware: "Ransomware Threats",
  phishing: "Phishing Threats",
  malware: "Malware Threats",
  "social-media": "Social Media Threats"
};

// Severity color mapping for UI
export const SEVERITY_COLORS: Record<ThreatSeverity, string> = {
  critical: "#DC2626", // red-600
  high: "#EA580C",     // orange-600
  medium: "#CA8A04",   // yellow-600
  low: "#16A34A"       // green-600
};

// Severity badge classes for Tailwind
export const SEVERITY_BADGE_CLASSES: Record<ThreatSeverity, string> = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
  low: "bg-green-100 text-green-800 border-green-200"
};

// Helper to get severity priority (for sorting)
export function getSeverityPriority(severity: ThreatSeverity): number {
  const priorities: Record<ThreatSeverity, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1
  };
  return priorities[severity] || 0;
}

// Helper to format date for display
export function formatThreatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return isoDate;
  }
}

// Helper to get relative time
export function getRelativeTime(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatThreatDate(isoDate);
  } catch {
    return isoDate;
  }
}

// ==================== Vulnerable Companies Report Types ====================

// Security scan results for a company
export interface CompanySecurityScan {
  score: number;
  criticalFindings: number;
  highFindings: number;
  exposedServices: string[];
  lastScan: string;
}

// AI-generated threat insights for a company
export interface CompanyThreatInsights {
  summary: string;
  likelyThreats: string[];
  attackVectors: string[];
}

// Individual company record in the vulnerability report
export interface VulnerableCompanyRecord {
  companyId: string;
  name: string;
  headquarters: string;
  country: string;
  industry: string;
  size: string;
  riskScore: number;
  riskTier: ThreatSeverity;
  threatInsights: CompanyThreatInsights;
  exposureFactors: string[];
  securityScan: CompanySecurityScan;
  lastUpdated: string;
}

// Region types for Global Vulnerability Report
export type RegionType = 'Europe' | 'North America' | 'APAC';

// Country-level grouping in the report
export interface CountryVulnerabilityData {
  country: string;
  countryCode: string;
  region: RegionType;
  overallRiskSummary: string;
  companies: VulnerableCompanyRecord[];
}

// Full vulnerable companies report response
export interface VulnerableCompaniesReport {
  generatedAt: string;
  reportId: string;
  countries: CountryVulnerabilityData[];
  notes: string[];
}
